import Card from "@/components/common/Card";
import { Switch } from "@headlessui/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

interface PrivacySettingsProps {
  user: any;
}

const PrivacySettings = ({ }: PrivacySettingsProps) => {
  const { t } = useTranslation();

  // Privacy settings
  const [privacySettings, setPrivacySettings] = useState({
    shareProgress: true,
    shareAchievements: true,
    allowAnalytics: true,
  });

  const handlePrivacyToggle = (setting: keyof typeof privacySettings) => {
    setPrivacySettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
    toast.success(t("settings.privacyUpdated"));
  };

  const privacyOptions = [
    {
      key: "shareProgress" as const,
      label: t("settings.shareProgress"),
      description: t("settings.shareProgressDescription"),
    },
    {
      key: "shareAchievements" as const,
      label: t("settings.shareAchievements"),
      description: t("settings.shareAchievementsDescription"),
    },
    {
      key: "allowAnalytics" as const,
      label: t("settings.allowAnalytics"),
      description: t("settings.allowAnalyticsDescription"),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
        {t("settings.privacy")}
      </h2>

      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.dataSharing")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t("settings.dataSharingDescription")}
          </p>
          <div className="space-y-4">
            {privacyOptions.map((option) => (
              <div key={option.key} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {option.description}
                  </p>
                </div>
                <Switch
                  checked={privacySettings[option.key]}
                  onChange={() => handlePrivacyToggle(option.key)}
                  className={`${
                    privacySettings[option.key]
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      privacySettings[option.key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.dataExport")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t("settings.dataExportDescription")}
          </p>
          <div className="flex space-x-4">
            <button className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
              {t("settings.exportData")}
            </button>
            <button className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
              {t("settings.downloadData")}
            </button>
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export default PrivacySettings;

